import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Progress } from "@/components/ui/progress";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import {
  AudioWaveform,
  BookOpen,
  Play,
  Clock,
  Star,
  Trophy,
  CheckCircle,
  Plus,
  Loader2,
  GraduationCap,
  Eye,
  User,
  Filter
} from "lucide-react";
import { toast } from "sonner";

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const INSTRUMENTS = [
  { value: "bass", label: "Bajo" },
  { value: "guitar", label: "Guitarra" },
  { value: "drums", label: "Batería" },
  { value: "keys", label: "Teclado" },
  { value: "vocals", label: "Voz" },
  { value: "theory", label: "Teoría" }
];

const LEVELS = [
  { value: "beginner", label: "Principiante", color: "text-green-400" },
  { value: "intermediate", label: "Intermedio", color: "text-yellow-400" },
  { value: "advanced", label: "Avanzado", color: "text-red-400" }
];

const TutorialsPage = () => {
  const { user, getAuthHeaders } = useAuth();
  
  const [tutorials, setTutorials] = useState([]);
  const [myProgress, setMyProgress] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("browse");
  
  // Filters
  const [filterInstrument, setFilterInstrument] = useState("all");
  const [filterLevel, setFilterLevel] = useState("all");
  
  // Selected tutorial
  const [selectedTutorial, setSelectedTutorial] = useState(null);
  const [showTutorialDialog, setShowTutorialDialog] = useState(false);
  
  // Create tutorial
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [creating, setCreating] = useState(false);
  const [newTutorial, setNewTutorial] = useState({
    title: "",
    description: "",
    instrument: "bass",
    level: "beginner",
    duration_minutes: 10,
    video_url: "",
    content_markdown: "",
    tags: ""
  });

  const fetchTutorials = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (filterInstrument !== "all") params.append("instrument", filterInstrument);
      if (filterLevel !== "all") params.append("level", filterLevel);
      
      const [tutorialsRes, progressRes] = await Promise.all([
        axios.get(`${API_URL}/tutorials?${params.toString()}`),
        axios.get(`${API_URL}/tutorials/my-progress`, { headers: getAuthHeaders() })
      ]);
      
      setTutorials(tutorialsRes.data);
      setMyProgress(progressRes.data);
    } catch (error) {
      console.error("Error fetching tutorials:", error);
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders, filterInstrument, filterLevel]);

  useEffect(() => {
    fetchTutorials();
  }, [fetchTutorials]);

  const openTutorial = async (tutorial) => {
    try {
      const res = await axios.get(`${API_URL}/tutorials/${tutorial.id}`);
      setSelectedTutorial(res.data);
      setShowTutorialDialog(true);
    } catch (error) {
      toast.error("Error al cargar tutorial");
    }
  };

  const markComplete = async (tutorialId) => {
    try {
      await axios.post(`${API_URL}/tutorials/${tutorialId}/progress?completed=true&progress_percent=100`, {}, { headers: getAuthHeaders() });
      toast.success("¡Tutorial completado!");
      fetchTutorials();
    } catch (error) {
      toast.error("Error");
    }
  };

  const rateTutorial = async (tutorialId, rating) => {
    try {
      await axios.post(`${API_URL}/tutorials/${tutorialId}/rate?rating=${rating}`, {}, { headers: getAuthHeaders() });
      toast.success("Calificación enviada");
      fetchTutorials();
    } catch (error) {
      toast.error("Error al calificar");
    }
  };

  const handleCreateTutorial = async () => {
    if (!newTutorial.title.trim() || !newTutorial.description.trim()) {
      toast.error("Completa título y descripción");
      return;
    }
    
    setCreating(true);
    try {
      await axios.post(`${API_URL}/tutorials`, {
        ...newTutorial,
        duration_minutes: parseInt(newTutorial.duration_minutes),
        tags: newTutorial.tags.split(",").map(t => t.trim()).filter(Boolean)
      }, { headers: getAuthHeaders() });
      
      toast.success("Tutorial creado (pendiente de revisión)");
      setShowCreateDialog(false);
      setNewTutorial({
        title: "", description: "", instrument: "bass", level: "beginner",
        duration_minutes: 10, video_url: "", content_markdown: "", tags: ""
      });
      fetchTutorials();
    } catch (error) {
      toast.error("Error al crear tutorial");
    } finally {
      setCreating(false);
    }
  };

  const getProgressForTutorial = (tutorialId) => {
    return myProgress.tutorials?.find(p => p.tutorial_id === tutorialId);
  };

  const TutorialCard = ({ tutorial }) => {
    const progress = getProgressForTutorial(tutorial.id);
    const levelInfo = LEVELS.find(l => l.value === tutorial.level);
    
    return (
      <Card 
        className="bg-[#18181b] border-white/10 cursor-pointer hover:border-primary/50 transition-all"
        onClick={() => openTutorial(tutorial)}
      >
        <CardContent className="p-5">
          <div className="flex items-start gap-4">
            <div className={`w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 ${
              tutorial.instrument === "bass" ? "bg-primary/10" :
              tutorial.instrument === "guitar" ? "bg-orange-500/10" :
              tutorial.instrument === "drums" ? "bg-red-500/10" :
              "bg-accent/10"
            }`}>
              <BookOpen className={`w-6 h-6 ${
                tutorial.instrument === "bass" ? "text-primary" :
                tutorial.instrument === "guitar" ? "text-orange-400" :
                tutorial.instrument === "drums" ? "text-red-400" :
                "text-accent"
              }`} />
            </div>
            
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <h3 className="font-semibold text-white truncate">{tutorial.title}</h3>
                {progress?.completed && (
                  <CheckCircle className="w-4 h-4 text-secondary flex-shrink-0" />
                )}
                {tutorial.is_verified && (
                  <Badge className="bg-secondary/10 text-secondary border-0 text-xs">Verificado</Badge>
                )}
              </div>
              
              <p className="text-sm text-gray-400 line-clamp-2 mb-3">{tutorial.description}</p>
              
              <div className="flex items-center gap-4 text-sm">
                <span className={levelInfo?.color}>{levelInfo?.label}</span>
                <span className="text-gray-500 flex items-center gap-1">
                  <Clock className="w-3 h-3" />
                  {tutorial.duration_minutes} min
                </span>
                <span className="text-gray-500 flex items-center gap-1">
                  <Eye className="w-3 h-3" />
                  {tutorial.views_count}
                </span>
                {tutorial.avg_rating > 0 && (
                  <span className="text-yellow-400 flex items-center gap-1">
                    <Star className="w-3 h-3" fill="currentColor" />
                    {tutorial.avg_rating}
                  </span>
                )}
              </div>
              
              {progress && !progress.completed && progress.progress_percent > 0 && (
                <Progress value={progress.progress_percent} className="mt-3 h-1" />
              )}
            </div>
          </div>
        </CardContent>
      </Card>
    );
  };

  return (
    <div className="min-h-screen bg-[#09090b]">
      {/* Header */}
      <header className="sticky top-0 z-50 glass-panel">
        <div className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/dashboard" className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-primary to-accent flex items-center justify-center">
                <AudioWaveform className="w-5 h-5 text-white" />
              </div>
              <span className="font-bold text-xl text-white font-['Manrope']">SteelMora</span>
            </Link>
            <span className="text-gray-600">/</span>
            <span className="text-white font-medium">Tutoriales</span>
          </div>
          
          <Button onClick={() => setShowCreateDialog(true)} variant="outline" className="border-white/10 text-gray-300 gap-2">
            <Plus className="w-4 h-4" />
            Crear Tutorial
          </Button>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-8">
        {/* Stats */}
        {myProgress && (
          <div className="grid grid-cols-3 gap-4 mb-8">
            <Card className="bg-[#18181b] border-white/10">
              <CardContent className="p-4 flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-secondary/10 flex items-center justify-center">
                  <Trophy className="w-5 h-5 text-secondary" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-white">{myProgress.completed_count || 0}</p>
                  <p className="text-xs text-gray-500">Completados</p>
                </div>
              </CardContent>
            </Card>
            <Card className="bg-[#18181b] border-white/10">
              <CardContent className="p-4 flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                  <BookOpen className="w-5 h-5 text-primary" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-white">{myProgress.in_progress_count || 0}</p>
                  <p className="text-xs text-gray-500">En progreso</p>
                </div>
              </CardContent>
            </Card>
            <Card className="bg-[#18181b] border-white/10">
              <CardContent className="p-4 flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-accent/10 flex items-center justify-center">
                  <GraduationCap className="w-5 h-5 text-accent" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-white">{tutorials.length}</p>
                  <p className="text-xs text-gray-500">Disponibles</p>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <div className="flex items-center justify-between mb-6">
            <TabsList className="bg-black/30">
              <TabsTrigger value="browse" className="data-[state=active]:bg-primary">
                Explorar
              </TabsTrigger>
              <TabsTrigger value="progress" className="data-[state=active]:bg-primary">
                Mi Progreso
              </TabsTrigger>
            </TabsList>
            
            {activeTab === "browse" && (
              <div className="flex items-center gap-2">
                <Select value={filterInstrument} onValueChange={setFilterInstrument}>
                  <SelectTrigger className="w-32 bg-[#18181b] border-white/10 text-white">
                    <SelectValue placeholder="Instrumento" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#18181b] border-white/10">
                    <SelectItem value="all" className="text-white">Todos</SelectItem>
                    {INSTRUMENTS.map(i => (
                      <SelectItem key={i.value} value={i.value} className="text-white">{i.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Select value={filterLevel} onValueChange={setFilterLevel}>
                  <SelectTrigger className="w-32 bg-[#18181b] border-white/10 text-white">
                    <SelectValue placeholder="Nivel" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#18181b] border-white/10">
                    <SelectItem value="all" className="text-white">Todos</SelectItem>
                    {LEVELS.map(l => (
                      <SelectItem key={l.value} value={l.value} className="text-white">{l.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>

          <TabsContent value="browse">
            {loading ? (
              <div className="flex justify-center py-20">
                <Loader2 className="w-8 h-8 text-primary animate-spin" />
              </div>
            ) : tutorials.length === 0 ? (
              <Card className="bg-[#18181b] border-white/10 border-dashed">
                <CardContent className="py-16 text-center">
                  <BookOpen className="w-16 h-16 text-gray-700 mx-auto mb-4" />
                  <h3 className="text-xl font-semibold text-white mb-2">No hay tutoriales</h3>
                  <p className="text-gray-400 mb-6">¡Sé el primero en crear contenido educativo!</p>
                  <Button onClick={() => setShowCreateDialog(true)} className="bg-primary gap-2">
                    <Plus className="w-4 h-4" />
                    Crear Tutorial
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <div className="grid gap-4">
                {tutorials.map(tutorial => <TutorialCard key={tutorial.id} tutorial={tutorial} />)}
              </div>
            )}
          </TabsContent>

          <TabsContent value="progress">
            {!myProgress.tutorials || myProgress.tutorials.length === 0 ? (
              <Card className="bg-[#18181b] border-white/10 border-dashed">
                <CardContent className="py-16 text-center">
                  <GraduationCap className="w-12 h-12 text-gray-700 mx-auto mb-3" />
                  <p className="text-gray-400">No has empezado ningún tutorial</p>
                </CardContent>
              </Card>
            ) : (
              <div className="grid gap-4">
                {myProgress.tutorials.map(progress => (
                  <Card key={progress.tutorial_id} className="bg-[#18181b] border-white/10">
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between mb-2">
                        <h3 className="font-medium text-white">{progress.tutorial_title}</h3>
                        {progress.completed ? (
                          <Badge className="bg-secondary/10 text-secondary border-0">Completado</Badge>
                        ) : (
                          <span className="text-sm text-gray-400">{progress.progress_percent}%</span>
                        )}
                      </div>
                      {!progress.completed && (
                        <Progress value={progress.progress_percent} className="h-2" />
                      )}
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </main>

      {/* Tutorial Detail Dialog */}
      <Dialog open={showTutorialDialog} onOpenChange={setShowTutorialDialog}>
        <DialogContent className="bg-[#18181b] border-white/10 max-w-2xl max-h-[90vh] overflow-y-auto">
          {selectedTutorial && (
            <>
              <DialogHeader>
                <DialogTitle className="text-white text-xl">{selectedTutorial.title}</DialogTitle>
              </DialogHeader>
              
              <div className="space-y-4 py-4">
                <div className="flex items-center gap-4 text-sm">
                  <Badge className={LEVELS.find(l => l.value === selectedTutorial.level)?.color}>
                    {LEVELS.find(l => l.value === selectedTutorial.level)?.label}
                  </Badge>
                  <span className="text-gray-400 flex items-center gap-1">
                    <Clock className="w-4 h-4" />
                    {selectedTutorial.duration_minutes} min
                  </span>
                  <span className="text-gray-400 flex items-center gap-1">
                    <User className="w-4 h-4" />
                    {selectedTutorial.author_name}
                  </span>
                </div>
                
                <p className="text-gray-300">{selectedTutorial.description}</p>
                
                {selectedTutorial.video_url && (
                  <div className="aspect-video bg-black rounded-lg flex items-center justify-center">
                    <a 
                      href={selectedTutorial.video_url} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-primary hover:underline flex items-center gap-2"
                    >
                      <Play className="w-8 h-8" />
                      Ver video
                    </a>
                  </div>
                )}
                
                {selectedTutorial.content_markdown && (
                  <div className="prose prose-invert max-w-none">
                    <div className="bg-black/30 rounded-lg p-4 text-sm text-gray-300 whitespace-pre-wrap">
                      {selectedTutorial.content_markdown}
                    </div>
                  </div>
                )}
                
                {/* Rating */}
                <div className="pt-4 border-t border-white/10">
                  <p className="text-sm text-gray-400 mb-2">Calificar este tutorial:</p>
                  <div className="flex gap-2">
                    {[1, 2, 3, 4, 5].map(star => (
                      <button
                        key={star}
                        onClick={() => rateTutorial(selectedTutorial.id, star)}
                        className="p-1 text-gray-600 hover:text-yellow-400 transition-colors"
                      >
                        <Star className="w-6 h-6" />
                      </button>
                    ))}
                  </div>
                </div>
              </div>
              
              <DialogFooter>
                <Button variant="outline" onClick={() => setShowTutorialDialog(false)} className="border-white/10 text-gray-300">
                  Cerrar
                </Button>
                <Button onClick={() => markComplete(selectedTutorial.id)} className="bg-secondary gap-2">
                  <CheckCircle className="w-4 h-4" />
                  Marcar completado
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Create Tutorial Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="bg-[#18181b] border-white/10 max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-white">Crear Tutorial</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <Input
              placeholder="Título *"
              value={newTutorial.title}
              onChange={(e) => setNewTutorial({ ...newTutorial, title: e.target.value })}
              className="bg-black/30 border-white/10 text-white"
            />
            
            <Textarea
              placeholder="Descripción *"
              value={newTutorial.description}
              onChange={(e) => setNewTutorial({ ...newTutorial, description: e.target.value })}
              className="bg-black/30 border-white/10 text-white min-h-[80px]"
            />
            
            <div className="grid grid-cols-3 gap-4">
              <Select value={newTutorial.instrument} onValueChange={(v) => setNewTutorial({ ...newTutorial, instrument: v })}>
                <SelectTrigger className="bg-black/30 border-white/10 text-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#18181b] border-white/10">
                  {INSTRUMENTS.map(i => (
                    <SelectItem key={i.value} value={i.value} className="text-white">{i.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              
              <Select value={newTutorial.level} onValueChange={(v) => setNewTutorial({ ...newTutorial, level: v })}>
                <SelectTrigger className="bg-black/30 border-white/10 text-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#18181b] border-white/10">
                  {LEVELS.map(l => (
                    <SelectItem key={l.value} value={l.value} className="text-white">{l.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              
              <Input
                type="number"
                placeholder="Duración (min)"
                value={newTutorial.duration_minutes}
                onChange={(e) => setNewTutorial({ ...newTutorial, duration_minutes: e.target.value })}
                className="bg-black/30 border-white/10 text-white"
              />
            </div>
            
            <Input
              placeholder="URL de video (YouTube, Vimeo...)"
              value={newTutorial.video_url}
              onChange={(e) => setNewTutorial({ ...newTutorial, video_url: e.target.value })}
              className="bg-black/30 border-white/10 text-white"
            />
            
            <Textarea
              placeholder="Contenido del tutorial (texto, instrucciones...)"
              value={newTutorial.content_markdown}
              onChange={(e) => setNewTutorial({ ...newTutorial, content_markdown: e.target.value })}
              className="bg-black/30 border-white/10 text-white min-h-[120px]"
            />
            
            <Input
              placeholder="Tags (separados por coma)"
              value={newTutorial.tags}
              onChange={(e) => setNewTutorial({ ...newTutorial, tags: e.target.value })}
              className="bg-black/30 border-white/10 text-white"
            />
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateDialog(false)} className="border-white/10 text-gray-300">
              Cancelar
            </Button>
            <Button onClick={handleCreateTutorial} disabled={creating} className="bg-primary">
              {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : "Crear"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TutorialsPage;
